/**
 * useDetailDate hook.
 */
import Content from '@shared/types/enums/content';
import DateUtils from '@shared/utils/date';
import DetailContext from '@shared/components/utils/context/detailContext';
import DescriptionContext from '@shared/components/utils/context/descriptionContext';
import Event from '@shared/classes/event';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { useContext, useMemo } from 'react';
import { useAppSelector } from '@shared/store/hooks';
import { routerSelector } from '@shared/store/slices/router.slice';
import Pages from '@pages';

/**
 * Constants.
 *
 * @constant {string} SEP - Separator.
 */
export const SEP = ' | ';

/**
 * Date item interface.
 */
interface DateItem {
  bold: boolean;
  key: string;
  text: string;
}

/**
 * Props interface.
 */
interface Props {
  type: 'detail' | 'description';
}

/**
 * Gets the duration of the event.
 *
 * @param {Event | null} asset - The event.
 * @returns {DateItem | null} The duration item.
 */
function getDuration(asset?: Event | null): DateItem | null {
  if (!asset) return null;
  const duration = (asset?.getDuration && asset?.getDuration()) || '';
  if (!duration) return null;
  return {
    bold: false,
    key: 'duration',
    text: duration,
  };
}

/**
 * Gets the start date of the event.
 *
 * @param {number | undefined} date - The start date.
 * @returns {DateItem | null} The start date item.
 */
function getStartDate(date?: number): DateItem | null {
  if (!date) return null;
  const dateObj = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const startTime = DateUtils.isSameDate(dateObj, today)
    ? TranslationManager.get('MISC.DATE.TODAY')
    : DateUtils.isSameDate(dateObj, yesterday)
    ? TranslationManager.get('MISC.DATE.YESTERDAY')
    : DateUtils.getTime({
        customDate: date,
        displaySeconds: false,
        displayMinutes: false,
        displayHours: false,
        displayDay: true,
        displayDayOfWeek: true,
        displayMonth: true,
        displayYear: true,
        ordinalDay: false,
        separator: '. ',
        trimDayOfTheWeek: true,
        trimMonth: true,
      });
  // Return the date item
  return {
    bold: false,
    key: 'startDate',
    text: startTime,
  };
}

/**
 * Gets the end date of the event.
 *
 * @param {number | undefined} date - The end date.
 * @returns {DateItem | null} The end date item.
 */
function getEndDate(date?: number): DateItem | null {
  if (!date) return null;
  const availableUntilDate = date + 7 * 24 * 60 * 60 * 1000;
  const endTime = `${TranslationManager.get(
    'PAGES.DETAIL.DATE.CONTENT_AVAILABLE_UNTIL',
    DateUtils.getTime({
      customDate: availableUntilDate,
      displaySeconds: false,
      displayMinutes: false,
      displayHours: false,
      displayDay: false,
      displayMonth: true,
      separator: '. ',
      trimMonth: true,
      ordinalDay: false,
    }),
    DateUtils.getTime({
      customDate: availableUntilDate,
      displaySeconds: false,
      displayMinutes: false,
      displayHours: false,
      displayDay: true,
      displayMonth: false,
      separator: '. ',
      trimMonth: true,
      ordinalDay: false,
    })
  )}.`;
  // Return the date item
  return {
    bold: true,
    key: 'endDate',
    text: endTime,
  };
}

/**
 * Gets the emission time of the event.
 *
 * @param {number | undefined} start - The start date.
 * @param {number | undefined} end - The end date.
 * @returns {DateItem | null} The emission time item.
 */
function getEmissionTime(start?: number, end?: number): DateItem | null {
  if (!start && !end) return null;
  const startTime = `${TranslationManager.get(
    'PAGES.DETAIL.DATE.BEGIN'
  )} ${DateUtils.getTime({
    customDate: start,
    displaySeconds: false,
    displayMinutes: true,
    displayHours: true,
  })} h`;
  const endTime = `${TranslationManager.get(
    'PAGES.DETAIL.DATE.END'
  )} ${DateUtils.getTime({
    customDate: end,
    displaySeconds: false,
    displayMinutes: true,
    displayHours: true,
  })} h`;
  // Return the date item
  return {
    bold: false,
    key: 'emissionTime',
    text: `${startTime} ${SEP} ${endTime}`,
  };
}

/**
 * Gets the date items for the event.
 *
 * @returns {DateItem[]} The date items.
 */
export default function useDateInfo(props?: Props): DateItem[] {
  const detailContext = useContext(DetailContext);
  const descriptionContext = useContext(DescriptionContext);
  const { asset, metadata } =
    props?.type === 'description' ? descriptionContext : detailContext;
  const { route } = useAppSelector(routerSelector);
  return useMemo(() => {
    if (
      (asset?.mocked || asset?.channel?.isFast) &&
      route.Page !== Pages.CHANNELS_GRID
    )
      return [];
    const duration = getDuration(asset);
    const startDate = getStartDate(asset?.date?.start);
    const endDate =
      !metadata?.isFutureDate && !metadata?.isCurrentDate
        ? getEndDate(asset?.date?.start)
        : null;
    const emissionTime =
      metadata?.isFutureDate || metadata?.isCurrentDate
        ? getEmissionTime(asset?.date?.start, asset?.date?.end)
        : null;
    const showEndDate = !(
      asset?.mocked &&
      !metadata?.isFutureDate &&
      !metadata?.isCurrentDate
    );
    return [
      showEndDate ? duration : '',
      startDate,
      showEndDate ? endDate : '',
      emissionTime,
    ].filter((item) => !!item);
  }, [asset, metadata]);
}
